import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useApp } from '../../context/AppContext';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TabBox from './TabBox';
import Ratings from '../molecules/Ratings';
import { display } from '../../data/display';
import { Review } from '@prisma/client';

interface ItemRatingsProps {
  type: string;
  data: any;
  access?: boolean;
  setLayer: (el: any) => void;
  apply: (id: string) => void;
}

const ItemRatings = (props: ItemRatingsProps) => {
  const { t } = useTranslation(['common', 'rating']);
  const [tab, setTab] = useState('');
  const { type, data } = props;
  const itemType = t(`rating:${type}`);
  const planData = display[type].platinum as any;
  const currReviews = data.reviews || [];

  const defaultRatingsData = planData.ratings || {};
  const defaultRatingsKeys = Object.keys(defaultRatingsData);
  const defaultRatingsByTab = planData.ratings[tab] || {};
  const defaultRatingsByTabKeys = Object.keys(defaultRatingsByTab);

  /**
   * Helper functions to get overall rating
   */
  const getRatingKeysByRole = (role?: string) => {
    const contact_type = role || tab;
    const defaultRatingsByTabKeys = planData.ratings[contact_type] || {};
    return Object.keys(defaultRatingsByTabKeys).reduce(
      (acc: any, curr: any) => [
        ...acc,
        ...Object.keys(defaultRatingsByTabKeys[curr]),
      ],
      []
    );
  };

  const getReviewsByRole = (role?: string) => {
    const contact_type = role || tab;
    const ratingKeysByTab = getRatingKeysByRole(contact_type) || [];
    const reviewsByType = currReviews.filter(
      (el: Review) =>
        el.contact_type === contact_type.toUpperCase() &&
        ratingKeysByTab.some((key: keyof Review) => (el[key] || 0) > 0)
    );
    return reviewsByType;
  };

  const getTabRatingByKey = (key: any, role?: any) => {
    const contact_type = role || tab;
    const reviewsByRole = getReviewsByRole(contact_type);
    const overall =
      reviewsByRole.reduce((acc: number, curr: any) => acc + curr[key], 0) /
      reviewsByRole.filter((obj: any) => obj[key] > 0).length;
    return isNaN(overall) ? 0 : overall;
  };

  const getOverAllRatingByRole = (role: string) => {
    const reviewsByRole = getReviewsByRole(role);
    const ratingKeysByTab = getRatingKeysByRole(role);
    const allKeyRatings = reviewsByRole.reduce(
      (acc: any, review: any) => [
        ...acc,
        ...ratingKeysByTab.filter((key) => review[key] > 0),
      ],
      []
    );
    const overall =
      reviewsByRole.reduce((acc: number, curr: any) => {
        return (
          acc + ratingKeysByTab.reduce((acc2, curr2) => acc2 + curr[curr2], 0)
        );
      }, 0) / allKeyRatings.length;
    return isNaN(overall) ? 0 : overall;
  };

  const getOverAllRating = (arr: any) => {
    const overall =
      arr.reduce((acc: number, curr: any) => acc + curr.value, 0) /
      arr.filter((obj: any) => obj.value > 0).length;
    return isNaN(overall) ? 0 : overall;
  };

  /**
   * Ratings data by business type & user role
   */
  const currRatings = defaultRatingsByTabKeys.map((key: string) => {
    const ratingKeys = Object.keys(defaultRatingsByTab[key]);
    const data = ratingKeys.map((el: string, i: number) => {
      return {
        id: el,
        parent: key,
        value: getTabRatingByKey(el),
        label: t(`rating:${el}`, { itemType }),
      };
    });
    const value = getOverAllRating(data);
    return {
      id: key,
      label: t(`rating:${key}`),
      value,
      data,
    };
  });

  /**
   * Ratings tabs
   */
  const currRatingsTabs = defaultRatingsKeys.map((key) => {
    const overAllByTab = getOverAllRatingByRole(key);
    return {
      id: key,
      label: t(`rating:${key}s`),
      icon: 'star',
      add: (
        <span>
          <b>{overAllByTab.toFixed(1)}</b>&nbsp;({getReviewsByRole(key).length})
        </span>
      ),
    };
  });

  return (
    <>
      {/* Star Rating */}
      <Row>
        <Col xs={8}>
          <TabBox
            bg
            hl={!!tab && `${t(`rating:overall`)} ${t(`rating:${tab}s`)}`}
            toggle
            offset
            onChange={(id) => setTab(id)}
            title={t(`rating:title`)}
            tabs={currRatingsTabs}
          >
            {currRatings.map((el: any, i: number) => (
              <Ratings
                key={i}
                label={el.label}
                value={el.value}
                data={el.data}
              />
            ))}
          </TabBox>
          <br />
          <br />
          <br />
        </Col>
      </Row>
    </>
  );
};

export default ItemRatings;
